import React, { createContext, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { LoadingIndicator } from './LoadingIndicator.tsx';

export const RootRenderer: React.MemoExoticComponent<
	(props: RootRendererProps) => React.JSX.Element
> = memo((props: RootRendererProps): React.JSX.Element => {
	const { batchSize, children, maxRenderIndex, onBatchRendered } = props;

	const seq = useBatchSequence(props);
	const [seqIndex, setSeqIndex] = useState(0);

	const lastSeqIndex = (seq?.length ?? Math.max(Math.ceil(maxRenderIndex / batchSize), 1)) - 1; // The last valid sequence index

	useEffect(() => {
		// eslint-disable-next-line compat/compat
		const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
		const cancelHandle = requestIdleCallback(() => {
			const done = seqIndex >= lastSeqIndex;

			onBatchRendered?.(done);

			if (!done) {
				setSeqIndex((pendingState) =>
					pendingState < lastSeqIndex ? pendingState + 1 : pendingState,
				);
			}
		});

		return () => {
			const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
			cancelIdleCallback(cancelHandle);
		};
	}, [seqIndex, lastSeqIndex, onBatchRendered, setSeqIndex]);

	const maxBatchRenderIndex = (seqIndex + 1) * batchSize;
	const shouldRenderNode = useCallback(
		(renderIndex: number) => {
			if (seq) {
				// Alternating sequence:
				const batchIndex = Math.floor(renderIndex / batchSize);
				if (batchIndex < seq.length) {
					return seq[batchIndex] <= seqIndex;
				}
			} else {
				// Linear sequence:
				return !maxBatchRenderIndex || renderIndex < maxBatchRenderIndex;
			}
		},
		[batchSize, maxBatchRenderIndex, seq, seqIndex],
	);

	// The first version of progressive rendering of ADF that went into production used the linear sequence and did not have a loading indicator.
	// But the alternating sequence renders from a linked anchor that's not in the first batch and is not a generally well-known user experience on the Web.
	// That's why we'll start with a loading indicator at the top in the case of the alternating sequence for now.
	const loadingIndicator =
		seq &&
		seq.length > 0 &&
		// The node at `renderIndex` 0 is the `doc`, which is never visualized, so the loading indicator is not necessary as soon as the node at `renderIndex` 1 is rendered:
		!shouldRenderNode(1) ? (
			<LoadingIndicator />
		) : null;

	return (
		<>
			{loadingIndicator}
			<NodeRendererContext.Provider value={shouldRenderNode}>
				{children}
			</NodeRendererContext.Provider>
		</>
	);
});
RootRenderer.displayName = 'RootRenderer';

type RootRendererProps = PropsWithChildren<{
	batchSize: number;
	maxRenderIndex: number;
	onBatchRendered?: (done: boolean) => void;
	targetBatchIndex?: number;
}>;

type NodeRendererContextType = ((renderIndex: number) => boolean | undefined) | undefined;
export const NodeRendererContext: React.Context<NodeRendererContextType> =
	createContext<NodeRendererContextType>(undefined);

function useBatchSequence({
	batchSize,
	maxRenderIndex,
	targetBatchIndex = 0,
}: Pick<RootRendererProps, 'batchSize' | 'maxRenderIndex' | 'targetBatchIndex'>) {
	type BatchSequence = {
		// What resets/re-initializes the sequence:
		batchSize: typeof batchSize;
		maxRenderIndex: typeof maxRenderIndex;

		// The sequence:
		batches: Array<number> | undefined;
	};

	const ref = useRef<BatchSequence>();

	if (ref.current?.batchSize !== batchSize || ref.current?.maxRenderIndex !== maxRenderIndex) {
		let batches = ref.current?.batches;

		if (targetBatchIndex) {
			// Alternating sequence:
			const batchCount = Math.max(Math.ceil(maxRenderIndex / batchSize), 1);

			if (batches?.length !== batchCount) {
				// Generate the sequence of batches to render, starting from `targetBatchIndex`:
				batches = new Array(batchCount);

				let seq = 0;

				batches[targetBatchIndex] = seq++;

				let above = targetBatchIndex - 1;
				let below = targetBatchIndex + 1;
				let yieldAbove = false;

				while (above >= 0 || below < batchCount) {
					if (yieldAbove) {
						if (above >= 0) {
							batches[above] = seq++;
							above--;
						}
					} else if (below < batchCount) {
						batches[below] = seq++;
						below++;
					}

					yieldAbove = !yieldAbove;
				}
			}
		} else {
			// Linear sequence:
			batches = undefined;
		}

		ref.current = {
			batchSize,
			maxRenderIndex,

			batches,
		};
	}

	return ref.current.batches;
}
